@if (project) {
  <button nbButton class="fab-button project" (click)="openProjectFullPage()">
    <i class="fa-thin fa-chart-simple"></i>
  </button>
}

@if (form) {
  <form [formGroup]="form" (ngSubmit)="onSubmit()">
    <nb-card [ngClass]="customNbCardClass" [nbSpinner]="loading">
      <nb-card-header>
        <div class="title" [innerHTML]="title"></div>
      </nb-card-header>
      <!-- FORM -->
      <nb-card-body>
        <div class="form-group">
          <!-- LABEL -->
          <div class="form-field">
            <label class="project" for="label">{{ 'Project label' | translate }}</label>
            <input
              nbInput
              id="label"
              type="text"
              formControlName="label"
              fullWidth
            >
          </div>
          <!-- PERIMETER -->
          <div class="form-field" [nbSpinner]="(perimeters$ | async)?.loading">
            <label class="project" for="perimeter">{{ 'Perimeter' | translate }}</label>
            <nb-select
              id="perimeter"
              formControlName="perimeter"
              fullWidth
            >
              <nb-select-label>{{ selectedPerimeterLabel }}</nb-select-label>
              @for (site of (perimeters$ | async)?.data; track site.id) {
                <nb-option-group [title]="site.name">
                  @for (perimeter of site.sub_perimeters; track perimeter.id) {
                    <nb-option [value]="perimeter.id">
                      {{ perimeter.name }}
                    </nb-option>
                  }
                </nb-option-group>
              }
            </nb-select>
          </div>
          <!-- STATUS -->
          <div class="form-field" [nbSpinner]="(statuses$ | async)?.loading">
            <label class="project" for="status">{{ 'Status' | translate }}</label>
            <nb-select
              id="status"
              formControlName="status"
              fullWidth
            >
              @for (s of (statuses$ | async)?.data; track s) {
                <nb-option [value]="s.id">
                  {{ s.name }}
                </nb-option>
              }
            </nb-select>
          </div>
          <!-- START DATE -->
          <div class="form-field pb-2">
            <label class="project" for="start_date">{{ 'Start date' | translate }}</label>
            <div class="datepicker-field">
              <input
                nbInput
                class="date-input"
                id="start_date"
                readonly
                [nbDatepicker]="startDatePicker"
                [value]="formatMonthYear(form.get('start_date')?.value)"
                (click)="!startDatePicker.isShown && startDatePicker.show()"
                fullWidth
              >
              <button type="button" class="datepicker-toggle" (click)="$event.preventDefault(); $event.stopPropagation(); startDatePicker.isShown ? startDatePicker.hide() : startDatePicker.show()">
                <fa-icon [icon]="['fal', 'calendar']"></fa-icon>
              </button>
            </div>
            <nb-datepicker
              #startDatePicker
              startView="year"
              [date]="toDateValue(form.get('start_date')?.value)"
              [visibleDate]="toDateValue(form.get('start_date')?.value) || toDateValue(minStartDate)"
              [min]="toDateValue(minStartDate)"
              [max]="toDateValue(maxStartDate)"
              (dateChange)="onProjectDateChange($event, 'start')"
            ></nb-datepicker>
          </div>
          <!-- END DATE -->
          <div class="form-field pb-2">
            <label class="project" for="end_date">{{ 'End date' | translate }}</label>
            <div class="datepicker-field">
              <input
                nbInput
                class="date-input"
                id="end_date"
                readonly
                [nbDatepicker]="endDatePicker"
                [value]="formatMonthYear(form.get('end_date')?.value)"
                (click)="!endDatePicker.isShown && endDatePicker.show()"
                fullWidth
              >
              <button type="button" class="datepicker-toggle" (click)="$event.preventDefault(); $event.stopPropagation(); endDatePicker.isShown ? endDatePicker.hide() : endDatePicker.show()">
                <fa-icon [icon]="['fal', 'calendar']"></fa-icon>
              </button>
            </div>
            <nb-datepicker
              #endDatePicker
              startView="year"
              [date]="toDateValue(form.get('end_date')?.value)"
              [visibleDate]="toDateValue(form.get('end_date')?.value) || toDateValue(minEndDate)"
              [min]="toDateValue(minEndDate)"
              [max]="toDateValue(maxEndDate)"
              (dateChange)="onProjectDateChange($event, 'end')"
            ></nb-datepicker>
          </div>
          <!-- PROJECT TYPE -->
          <div class="form-field" [nbSpinner]="(projectTypes$ | async)?.loading">
            <label class="project" for="project_type">{{ 'Project type' | translate }}</label>
            <nb-select
              id="project_type"
              formControlName="project_type"
              fullWidth
            >
              @for (projectType of (projectTypes$ | async)?.data; track projectType.id) {
                <nb-option [value]="projectType.id">
                  {{ projectType.name }}
                </nb-option>
              }
            </nb-select>
          </div>
          <!-- RELATED FLOORING AREA -->
          <div class="form-field">
            <label class="project">{{ "Project related area" | translate }}</label>
            <div>
              <span>
                @if (area()) {
                  {{ area() }} {{ "m²" | translate }}
                } @else {
                  -
                }
              </span>
            </div>
          </div>
          <!-- TOTAL AMOUNT - INVESTMENTS -->
          <div class="form-field display-with-link" (click)="openInvestmentsList()">
            <label class="project" for="budget">{{ "Total amount" | translate }}</label>
            <div>
              @if (form.get('investments')?.value?.length) {
                <span>{{ totalAmount() | ngxCurrency:currency }} ({{ form.get('investments')?.value?.length }} {{ "Investments" | translate}})</span>
              } @else {
                <span>-</span>
              }
              <i class="far fa-chevron-right fal"></i>
            </div>
          </div>
          <!-- GLOBAL GAINS -->
          <div class="form-field display-with-link" (click)="openGainsForm()">
            <label class="project">{{ "Global gain of bouquet of works" | translate }}</label>
            <div>
              @if (totalGains()) {
                <span>{{ totalGains() | thousandsSeparator }} {{ "kWh" | translate }}</span>
              } @else {
                <span>-</span>
              }
              <i class="far fa-chevron-right fal"></i>
            </div>
          </div>
          <!-- INITIATIVES -->
          <div class="form-field display-with-link" (click)="openInitiativesList()">
            <label class="project" for="project_initiatives">{{ "Initiatives" | translate }}</label>
            <div>
              @if (form.get('project_initiatives')?.value?.length) {
                <span>{{ form.get('project_initiatives').value.length }}</span>
              } @else {
                <span>-</span>
              }
              <i class="far fa-chevron-right fal"></i>
            </div>
          </div>
          <!-- COMMENTS -->
          <div class="form-field">
            <label class="project" for="comments">{{ "Comments" | translate }}</label>
            <textarea nbInput id="comments" type="text" formControlName="comments" fullWidth rows="5"></textarea>
          </div>
        </div>
      </nb-card-body>
      <!-- CANCEL OR SUBMIT -->
      @if (anyFieldValueChanged()) {
        <nb-card-footer class="project">
          <button nbButton type="submit" [disabled]="!isFormValid()">
            {{ "Save" | translate }}
          </button>
        </nb-card-footer>
      }
    </nb-card>
  </form>
}
